import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteLayout from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  CITY_DATA,
  HOME_CONTENT,
  IMAGES,
  SERVICES,
  getCityServicePath,
  getSpecialServicePath,
  getServicePath,
} from "@/data/siteContent";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: true },
};

const HomePage = ({ language = "fr" }) => {
  const content = HOME_CONTENT[language] || HOME_CONTENT.fr;
  const metaTitle = content.metaTitle || "Réparation pavé uni Rive-Sud";
  const metaDescription =
    content.metaDescription ||
    "Réparation et installation de pavé uni sur la Rive-Sud.";

  usePageMeta(metaTitle, metaDescription);
  const isFr = language === "fr";
  const heroImageBase = IMAGES.heroInstallation;
  const heroImageSrcSet = `${heroImageBase}&w=640&q=60 640w, ${heroImageBase}&w=960&q=65 960w, ${heroImageBase}&w=1400&q=70 1400w`;
  const serviceCards = [
    SERVICES.repair,
    SERVICES.installation,
    SERVICES.cleaning,
    SERVICES.sealing,
  ];
  const serviceLinks = {
    repair: getServicePath(language, "repair"),
    installation: getServicePath(language, "installation"),
    cleaning: getSpecialServicePath(language, "cleaning"),
    sealing: getSpecialServicePath(language, "sealing"),
  };

  return (
    <SiteLayout language={language}>
      <section className="bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <motion.div {...fadeUp} className="space-y-6">
            <p
              className="text-xs uppercase tracking-[0.4em] text-secondary"
              data-testid="home-hero-eyebrow"
            >
              {content.hero.eyebrow}
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-primary leading-tight"
              data-testid="home-hero-title"
            >
              {content.hero.title}
            </h1>
            <p
              className="text-lg text-muted-foreground"
              data-testid="home-hero-subtitle"
            >
              {content.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                className="rounded-none uppercase tracking-wider font-bold"
                data-testid="home-hero-primary-cta"
              >
                <Link to={isFr ? "/fr/contact" : "/en/contact"}>
                  {content.hero.primaryCta}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-none uppercase tracking-wider font-bold"
                data-testid="home-hero-secondary-cta"
              >
                <Link to={isFr ? "/fr/services/reparation-pave-uni" : "/en/services/paver-repair"}>
                  {content.hero.secondaryCta}
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
              {content.trust.map((item, index) => (
                <div key={item.value} data-testid={`home-trust-${index + 1}`}>
                  <p className="text-2xl font-bold text-primary">{item.value}</p>
                  <p className="text-sm text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <img
              src={`${heroImageBase}&w=1400&q=70`}
              srcSet={heroImageSrcSet}
              sizes="(max-width: 1024px) 90vw, 520px"
              alt={isFr ? "Installation pavé uni" : "Paver installation"}
              className="w-full h-[420px] object-cover shadow-2xl"
              loading="eager"
              fetchPriority="high"
              data-testid="home-hero-image"
            />
            <div className="absolute -bottom-6 -left-6 bg-white shadow-lg p-4 border border-border" data-testid="home-hero-badge">
              <p className="text-xs uppercase tracking-[0.3em] text-secondary hero-overlay-text">
                {isFr ? "RIVE-SUD" : "SOUTH SHORE"}
              </p>
              <p className="text-sm font-semibold text-primary hero-overlay-text">
                {isFr
                  ? "Service résidentiel personnalisé"
                  : "Personalized residential service"}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp} className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="home-services-eyebrow">
                {isFr ? "Services" : "Services"}
              </p>
              <h2 className="text-3xl font-bold text-primary" data-testid="home-services-title">
                {isFr
                  ? "Solutions complètes pour votre pavé uni"
                  : "Complete solutions for your pavers"}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-lg" data-testid="home-services-subtitle">
              {isFr
                ? "De la réparation ciblée à l'installation complète, nous couvrons chaque étape pour un rendu durable."
                : "From targeted repairs to full installations, we handle every step for lasting results."}
            </p>
          </motion.div>

          <div className="grid gap-6 mt-12 md:grid-cols-2 lg:grid-cols-3">
            {serviceCards.map((service, index) => (
              <motion.div
                {...fadeUp}
                key={service.key}
                className={`border border-border bg-white p-6 shadow-sm hover:shadow-lg transition-all ${
                  index === 0 ? "lg:col-span-2" : ""
                }`}
                data-testid={`home-service-card-${service.key}`}
              >
                <p className="text-xs uppercase tracking-[0.3em] text-secondary">
                  {isFr ? "Service" : "Service"}
                </p>
                <h3 className="text-xl font-bold text-primary mt-3">
                  {isFr ? service.nameFr : service.nameEn}
                </h3>
                <p className="text-sm text-muted-foreground mt-3">
                  {isFr ? service.descriptionFr : service.descriptionEn}
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none uppercase tracking-wider font-bold mt-6"
                  data-testid={`home-service-card-${service.key}-link`}
                >
                  <Link to={serviceLinks[service.key]}>
                    {isFr ? "Voir le service" : "View service"}
                  </Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp} className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="home-process-eyebrow">
                {isFr ? "Méthode" : "Process"}
              </p>
              <h2 className="text-3xl font-bold" data-testid="home-process-title">
                {isFr ? "Une méthode claire, des résultats solides" : "A clear method, solid results"}
              </h2>
              <p className="text-sm text-slate-200 mt-4" data-testid="home-process-subtitle">
                {isFr
                  ? "Chaque intervention est structurée pour offrir un pavé uni stable et esthétique, adapté aux réalités de la Rive-Sud."
                  : "Every intervention is structured to deliver stable, beautiful pavers tailored to South Shore conditions."}
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-3">
              {content.process.map((step, index) => (
                <div key={step.title} className="border border-slate-700 p-5" data-testid={`home-process-step-${index + 1}`}>
                  <p className="text-xl font-bold text-secondary">{`0${index + 1}`}</p>
                  <h3 className="mt-3 font-semibold">{step.title}</h3>
                  <p className="text-sm text-slate-200 mt-2">{step.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp} className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="home-cities-eyebrow">
                {isFr ? "Villes" : "Cities"}
              </p>
              <h2 className="text-3xl font-bold text-primary" data-testid="home-cities-title">
                {isFr
                  ? "Desservis sur la Rive-Sud"
                  : "Serving the South Shore"}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-lg" data-testid="home-cities-subtitle">
              {isFr
                ? "Accédez aux pages locales pour des informations spécifiques à votre ville."
                : "Access local pages for information tailored to your city."}
            </p>
          </motion.div>

          <div className="grid gap-6 mt-12 md:grid-cols-2 lg:grid-cols-3">
            {CITY_DATA.map((city) => (
              <motion.div
                {...fadeUp}
                key={city.slug}
                className="border border-border bg-white p-6 shadow-sm"
                data-testid={`home-city-card-${city.slug}`}
              >
                <h3 className="text-xl font-bold text-primary" data-testid={`home-city-${city.slug}-title`}>
                  {isFr ? city.nameFr : city.nameEn}
                </h3>
                <p className="text-sm text-muted-foreground mt-2" data-testid={`home-city-${city.slug}-note`}>
                  {isFr ? city.landmarkFr : city.landmarkEn}
                </p>
                <div className="flex flex-col gap-2 mt-4">
                  <Link
                    to={getCityServicePath(language, city.slug, "repair")}
                    className="text-sm font-semibold text-secondary"
                    data-testid={`home-city-${city.slug}-repair-link`}
                  >
                    {isFr
                      ? "Réparation de pavé uni"
                      : "Paver repair"}
                  </Link>
                  <Link
                    to={getCityServicePath(language, city.slug, "installation")}
                    className="text-sm font-semibold text-secondary"
                    data-testid={`home-city-${city.slug}-installation-link`}
                  >
                    {isFr
                      ? "Installation de pavé uni"
                      : "Paver installation"}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp} className="space-y-6">
            <p className="font-accent text-xl text-primary" data-testid="home-testimonial-quote">
              “{content.testimonial.quote}”
            </p>
            <p className="text-sm text-muted-foreground" data-testid="home-testimonial-author">
              {content.testimonial.author}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-3xl font-bold text-primary" data-testid="home-final-cta-title">
              {content.cta.title}
            </h2>
            <p className="text-muted-foreground" data-testid="home-final-cta-text">
              {content.cta.text}
            </p>
            <Button
              asChild
              className="rounded-none uppercase tracking-wider font-bold"
              data-testid="home-final-cta-button"
            >
              <Link to={isFr ? "/fr/contact" : "/en/contact"}>
                {content.cta.button}
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default HomePage;